import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  ShieldCheck, ShieldAlert, Clock, CheckCircle2, XCircle, Eye, FileText, 
  ArrowUp, Search, Filter, ExternalLink, User, Building, Globe, Package,
  Loader2, ChevronRight, AlertTriangle, Download, ArrowLeft, MapPin, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const TIER_INFO = {
  tier_1_pan_only: {
    name: 'Tier 1: PAN Only',
    shortName: 'Tier 1',
    description: 'Lead generation only. No transactions.',
    color: 'bg-gray-100 text-gray-800 border-gray-200',
    icon: ShieldAlert
  },
  tier_2_gst_enrollment: {
    name: 'Tier 2: GST Enrollment',
    shortName: 'Tier 2',
    description: 'Intra-state transactions with limits',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: ShieldCheck
  },
  tier_3_full_gst: {
    name: 'Tier 3: Full GST',
    shortName: 'Tier 3',
    description: 'Pan-India transactions',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: ShieldCheck
  },
  tier_4_gst_iec: {
    name: 'Tier 4: International',
    shortName: 'Tier 4',
    description: 'International transactions enabled',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Globe
  },
  tier_5_gst_exempt: {
    name: 'Tier 5: GST Exempt',
    shortName: 'Tier 5',
    description: 'Pan-India, no GST charged (exempt by law)',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: ShieldCheck
  }
};

const AdminVendorCompliance = () => {
  const { apiCall, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterTier, setFilterTier] = useState('all');
  const [filterUpgradeTo, setFilterUpgradeTo] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [reviewDialog, setReviewDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [reviewData, setReviewData] = useState({
    approved: true,
    admin_notes: '',
    rejection_reason: ''
  });
  
  // GST Identity Queue State
  const [activeTab, setActiveTab] = useState('tier-upgrades');
  const [gstQueue, setGstQueue] = useState([]);
  const [gstQueueCounts, setGstQueueCounts] = useState({});
  const [gstQueueLoading, setGstQueueLoading] = useState(false);
  const [selectedGstItem, setSelectedGstItem] = useState(null);
  const [gstDetailData, setGstDetailData] = useState(null);
  const [gstDetailLoading, setGstDetailLoading] = useState(false);
  const [gstActionDialog, setGstActionDialog] = useState({ open: false, type: null });
  const [gstAdminNotes, setGstAdminNotes] = useState('');
  const [gstRejectionReason, setGstRejectionReason] = useState('');
  const [gstUpdateProfile, setGstUpdateProfile] = useState(true);
  const [gstActionLoading, setGstActionLoading] = useState(false);
  const [gstStatusFilter, setGstStatusFilter] = useState('PENDING_MANUAL');
  const [gstPriorityFilter, setGstPriorityFilter] = useState('all');

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get all vendors with detailed info (includes bank, address, contact)
      const vendorsResponse = await apiCall('/api/vendors/admin/detailed');
      
      // Get all compliance records
      const complianceResponse = await apiCall('/api/vendor-compliance/admin/all');
      const complianceData = complianceResponse.data || complianceResponse || [];
      
      // Merge vendor data with compliance
      const vendorsWithCompliance = vendorsResponse.map(vendor => {
        const compliance = complianceData.find(c => c.vendor_id === vendor.id);
        return {
          ...vendor,
          compliance: compliance || {
            vendor_id: vendor.id,
            current_tier: 'tier_1_pan_only',
            status: 'approved',
            tier_verified: false
          }
        };
      });
      
      setVendors(vendorsWithCompliance);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  // Fetch GST Identity Queue
  const fetchGstQueue = useCallback(async () => {
    setGstQueueLoading(true);
    try {
      const params = new URLSearchParams();
      if (gstStatusFilter && gstStatusFilter !== 'all') params.append('status_filter', gstStatusFilter);
      if (gstPriorityFilter && gstPriorityFilter !== 'all') params.append('priority_filter', gstPriorityFilter);
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/identity/admin/tax-verification-queue?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setGstQueue(data.items || []);
        setGstQueueCounts(data.counts || {});
      } else {
        toast.error('Failed to fetch GST verification queue');
      }
    } catch (error) {
      console.error('Error fetching GST queue:', error);
    } finally {
      setGstQueueLoading(false);
    }
  }, [token, gstStatusFilter, gstPriorityFilter]);

  const fetchGstDetail = async (queueId) => {
    setGstDetailLoading(true);
    setSelectedGstItem(queueId);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/identity/admin/tax-verification-queue/${queueId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
        }
      );
      
      
      if (response.ok) {
        const data = await response.json();
        setGstDetailData(data);
      } else {
        const errorText = await response.text();
        console.error('GST detail error:', errorText);
        toast.error('Failed to fetch verification details');
        setSelectedGstItem(null);
      }
    } catch (error) {
      console.error('Error fetching GST detail:', error);
      toast.error('Error loading verification details');
      setSelectedGstItem(null);
    } finally {
      setGstDetailLoading(false);
    }
  };

  const handleGstAction = async () => {
    if (gstActionDialog.type === 'reject' && !gstRejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setGstActionLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/identity/admin/tax-verification-queue/${selectedGstItem}/action`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: gstActionDialog.type,
            update_profile: gstUpdateProfile,
            admin_notes: gstAdminNotes,
            rejection_reason: gstActionDialog.type === 'reject' ? gstRejectionReason : null,
          }),
        }
      );
      
      if (response.ok) {
        toast.success(`GST verification ${gstActionDialog.type === 'approve' ? 'approved' : 'rejected'} successfully`);
        setGstActionDialog({ open: false, type: null });
        setGstAdminNotes('');
        setGstRejectionReason('');
        setSelectedGstItem(null);
        setGstDetailData(null);
        fetchGstQueue();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      console.error('Error processing GST action:', error);
      toast.error('Error processing verification action');
    } finally {
      setGstActionLoading(false);
    }
  };

  const getGstPriorityBadge = (priority) => {
    if (priority === 'HIGH') {
      return <Badge variant="destructive" className="gap-1"><AlertTriangle className="h-3 w-3" />High</Badge>;
    }
    return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Low</Badge>;
  };

  const getGstStatusBadge = (status) => {
    switch (status) {
      case 'PENDING_MANUAL':
        return <Badge variant="outline" className="border-yellow-500 text-yellow-600">Pending</Badge>;
      case 'APPROVED':
        return <Badge className="bg-green-500">Approved</Badge>;
      case 'REJECTED':
        return <Badge variant="destructive">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getMatchScoreColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  useEffect(() => {
    fetchVendors();
    // Also fetch GST queue counts on initial load for the badge
    fetchGstQueue();
  }, [fetchVendors]);

  useEffect(() => {
    if (activeTab === 'gst-identity') {
      fetchGstQueue();
    }
  }, [activeTab, fetchGstQueue]);

  useEffect(() => {
    applyFilters();
  }, [vendors, filterStatus, filterTier, filterUpgradeTo, searchQuery]);

  const applyFilters = () => {
    let filtered = [...vendors];
    
    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(v => v.compliance?.status === filterStatus);
    }
    
    // Current tier filter
    if (filterTier !== 'all') {
      filtered = filtered.filter(v => v.compliance?.current_tier === filterTier);
    }
    
    // Requested tier filter
    if (filterUpgradeTo !== 'all') {
      filtered = filtered.filter(v => v.compliance?.requested_tier === filterUpgradeTo);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(v => 
        (v.business_name || '').toLowerCase().includes(query) ||
        (v.name || '').toLowerCase().includes(query) ||
        (v.email || '').toLowerCase().includes(query) ||
        (v.compliance?.gstin || '').toLowerCase().includes(query)
      );
    }
    
    setFilteredVendors(filtered);
  };

  const handleReviewClick = (vendor) => {
    setSelectedVendor(vendor);
    setReviewData({
      approved: true,
      admin_notes: '',
      rejection_reason: ''
    });
    setReviewDialog(true);
  };

  const handleSubmitReview = async () => {
    if (!reviewData.approved && !reviewData.rejection_reason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    setProcessing(true);
    try {
      const requestedTier = selectedVendor.compliance?.requested_tier || selectedVendor.compliance?.current_tier;
      
      await apiCall(`/api/vendor-compliance/admin/verify/${selectedVendor.id}`, {
        method: 'POST',
        body: JSON.stringify({
          approved: reviewData.approved,
          verified_tier: requestedTier,
          admin_notes: reviewData.admin_notes,
          rejection_reason: reviewData.rejection_reason,
          pan_verified: true,
          enrollment_verified: selectedVendor.compliance?.enrollment_verified || false,
          gst_verified: selectedVendor.compliance?.gst_verified || false,
          iec_verified: requestedTier === 'tier_4_gst_iec'
        })
      });
      
      toast.success(`Vendor compliance ${reviewData.approved ? 'approved' : 'rejected'}`);
      setReviewDialog(false);
      fetchVendors();
    } catch (error) {
      console.error('Error processing review:', error);
      toast.error('Failed to process review');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending Review', variant: 'secondary', icon: Clock, className: 'bg-amber-100 text-amber-800' },
      approved: { label: 'Approved', variant: 'default', icon: CheckCircle2, className: 'bg-green-100 text-green-800' },
      rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle, className: 'bg-red-100 text-red-800' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`flex items-center gap-1 ${config.className}`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getTierBadge = (tier) => {
    const info = TIER_INFO[tier] || TIER_INFO.tier_1_pan_only;
    return (
      <Badge className={`${info.color}`}>
        {info.shortName}
      </Badge>
    );
  };

  const getUpgradeInfo = (vendor) => {
    const currentTier = vendor.compliance?.current_tier;
    const requestedTier = vendor.compliance?.requested_tier;
    
    if (!requestedTier || requestedTier === currentTier) return null;
    
    const currentInfo = TIER_INFO[currentTier];
    const requestedInfo = TIER_INFO[requestedTier];
    
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-500">{currentInfo?.shortName}</span>
        <ArrowUp className="h-4 w-4 text-primary" />
        <span className="font-medium text-primary">{requestedInfo?.shortName}</span>
      </div>
    );
  };

  // Count by status for tabs
  const pendingCount = vendors.filter(v => v.compliance?.status === 'pending').length;
  const approvedCount = vendors.filter(v => v.compliance?.status === 'approved').length;
  const rejectedCount = vendors.filter(v => v.compliance?.status === 'rejected').length;

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-20 md:pb-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold">Vendor Compliance Management</h1>
          <p className="text-gray-500 mt-1">Review and approve vendor compliance upgrade requests</p>
        </div>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="tier-upgrades" className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4" />
              Tier Upgrades ({pendingCount})
            </TabsTrigger>
            <TabsTrigger value="gst-identity" className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              GST Identity Queue ({gstQueueCounts['PENDING_MANUAL'] || 0})
            </TabsTrigger>
          </TabsList>

          {/* Tier Upgrades Tab */}
          <TabsContent value="tier-upgrades" className="space-y-6 mt-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Pending</p>
                      <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
                    </div>
                    <Clock className="h-8 w-8 text-amber-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Approved</p>
                      <p className="text-2xl font-bold text-green-600">{approvedCount}</p>
                    </div>
                    <CheckCircle2 className="h-8 w-8 text-green-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Rejected</p>
                      <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
                    </div>
                    <XCircle className="h-8 w-8 text-red-200" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total</p>
                      <p className="text-2xl font-bold">{vendors.length}</p>
                    </div>
                    <Building className="h-8 w-8 text-gray-200" />
                  </div>
                </CardContent>
              </Card>
            </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, or GSTIN..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              {/* Status Filter */}
              <div className="w-full md:w-40">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Current Tier Filter */}
              <div className="w-full md:w-40">
                <Select value={filterTier} onValueChange={setFilterTier}>
                  <SelectTrigger>
                    <SelectValue placeholder="Current Tier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="tier_1_pan_only">Tier 1</SelectItem>
                    <SelectItem value="tier_2_gst_enrollment">Tier 2</SelectItem>
                    <SelectItem value="tier_3_full_gst">Tier 3</SelectItem>
                    <SelectItem value="tier_4_gst_iec">Tier 4</SelectItem>
                    <SelectItem value="tier_5_gst_exempt">Tier 5 (GST Exempt)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vendors List */}
        {loading ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="text-gray-500 mt-2">Loading vendors...</p>
            </CardContent>
          </Card>
        ) : filteredVendors.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShieldAlert className="h-12 w-12 mx-auto text-gray-300" />
              <p className="text-gray-500 mt-2">No vendors found matching your filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredVendors.map((vendor) => (
              <Card key={vendor.id} className={vendor.compliance?.status === 'pending' ? 'border-amber-200 bg-amber-50/30' : ''}>
                <CardContent className="pt-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    {/* Vendor Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold truncate">
                          {vendor.business_name || vendor.name || 'Unnamed Vendor'}
                        </h3>
                        {getStatusBadge(vendor.compliance?.status)}
                        {getTierBadge(vendor.compliance?.current_tier)}
                      </div>
                      
                      <p className="text-sm text-gray-500 mb-3">{vendor.email}</p>
                      
                      {/* Upgrade Request */}
                      {vendor.compliance?.status === 'pending' && getUpgradeInfo(vendor) && (
                        <div className="p-3 bg-blue-50 rounded-lg mb-3">
                          <p className="text-sm font-medium text-blue-800 mb-1">Upgrade Request</p>
                          {getUpgradeInfo(vendor)}
                        </div>
                      )}
                      
                      {/* Compliance Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                        {vendor.compliance?.pan_number && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">PAN:</span>
                            <span className="font-medium">{vendor.compliance.pan_number}</span>
                            {vendor.compliance.pan_verified && (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        )}
                        
                        {vendor.compliance?.gst_enrollment_id && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">Enrollment:</span>
                            <span className="font-medium">{vendor.compliance.gst_enrollment_id}</span>
                            {vendor.compliance.enrollment_verified && (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        )}
                        
                        {vendor.compliance?.gstin && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">GSTIN:</span>
                            <span className="font-medium">{vendor.compliance.gstin}</span>
                            {vendor.compliance.gst_verified && (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        )}
                        
                        {vendor.compliance?.iec_code && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">IEC:</span>
                            <span className="font-medium">{vendor.compliance.iec_code}</span>
                            {vendor.compliance.iec_verified && (
                              <CheckCircle2 className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                        )}
                        
                        {vendor.compliance?.lut_acknowledged && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">LUT:</span>
                            <span className="font-medium text-green-600">Acknowledged</span>
                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                          </div>
                        )}
                      </div>
                      
                      {/* Documents */}
                      {(vendor.compliance?.documents?.length > 0 || vendor.compliance?.gst_doc || vendor.compliance?.iec_doc) && (
                        <div className="mt-3 pt-3 border-t">
                          <p className="text-sm font-medium mb-2">Uploaded Documents:</p>
                          <div className="flex flex-wrap gap-2">
                            {vendor.compliance?.gst_doc && (
                              <a 
                                href={vendor.compliance.gst_doc} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200"
                              >
                                <FileText className="h-4 w-4" />
                                GST Certificate
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                            {vendor.compliance?.iec_doc && (
                              <a 
                                href={vendor.compliance.iec_doc} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200"
                              >
                                <FileText className="h-4 w-4" />
                                IEC Certificate
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                            {vendor.compliance?.documents?.map((doc, idx) => (
                              <a 
                                key={doc.id || doc.name || `${doc.title || "doc"}-${idx}`}
                                href={doc.document_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-sm hover:bg-gray-200"
                              >
                                <FileText className="h-4 w-4" />
                                {doc.document_type}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Admin Notes */}
                      {vendor.compliance?.admin_notes && (
                        <div className="mt-3 p-2 bg-gray-50 rounded text-sm">
                          <span className="font-medium">Admin Notes: </span>
                          {vendor.compliance.admin_notes}
                        </div>
                      )}
                      
                      {/* Rejection Reason */}
                      {vendor.compliance?.status === 'rejected' && vendor.compliance?.rejection_reason && (
                        <div className="mt-3 p-2 bg-red-50 rounded text-sm">
                          <span className="font-medium text-red-800">Rejection Reason: </span>
                          <span className="text-red-700">{vendor.compliance.rejection_reason}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex-shrink-0">
                      <Button
                        onClick={() => handleReviewClick(vendor)}
                        variant={vendor.compliance?.status === 'pending' ? 'default' : 'outline'}
                        size="sm"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {vendor.compliance?.status === 'pending' ? 'Review' : 'View'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          {/* GST Identity Queue Tab */}
          <TabsContent value="gst-identity" className="space-y-6 mt-6">
            {/* GST Queue Detail View */}
            {selectedGstItem && gstDetailData ? (
              <div className="space-y-6">
                {/* Back Button */}
                <Button 
                  variant="ghost" 
                  onClick={() => { setSelectedGstItem(null); setGstDetailData(null); }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to Queue
                </Button>

                {/* Status and Priority */}
                <div className="flex gap-4">
                  {getGstStatusBadge(gstDetailData.queue_item.status)}
                  {getGstPriorityBadge(gstDetailData.queue_item.priority)}
                </div>

                {/* Mismatch Reasons Alert */}
                {gstDetailData.queue_item.reasons?.length > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <p className="font-medium mb-2">Verification Issues Found:</p>
                      <ul className="list-disc list-inside space-y-1">
                        {gstDetailData.queue_item.reasons.map((reason, idx) => (
                          <li key={reason.id || reason.name || `${reason.title || "reason"}-${idx}`}>{reason}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {/* Side-by-Side Comparison */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* GST API Data */}
                  <Card className="border-blue-200">
                    <CardHeader className="bg-blue-50">
                      <CardTitle className="flex items-center gap-2 text-blue-700">
                        <FileText className="h-5 w-5" />
                        GST Portal Data
                      </CardTitle>
                      <CardDescription>Official data from GST API</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground">GSTIN</label>
                        <p className="font-mono font-medium">{gstDetailData.comparison.gst_api.gstin}</p>
                      </div>
                      <div className={gstDetailData.comparison.gst_api.legal_name !== gstDetailData.comparison.vendor_profile.legal_name ? 'bg-red-50 p-2 rounded border border-red-200' : ''}>
                        <label className="text-xs text-muted-foreground">Legal Name</label>
                        <p className="font-medium">{gstDetailData.comparison.gst_api.legal_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Trade Name</label>
                        <p className="font-medium">{gstDetailData.comparison.gst_api.trade_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Constitution</label>
                        <p className="font-medium">{gstDetailData.comparison.gst_api.constitution_of_business || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">State Code</label>
                        <p className="font-medium">{gstDetailData.comparison.gst_api.state_code || '-'}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Vendor Profile Data */}
                  <Card className="border-green-200">
                    <CardHeader className="bg-green-50">
                      <CardTitle className="flex items-center gap-2 text-green-700">
                        <Building className="h-5 w-5" />
                        Vendor Profile
                      </CardTitle>
                      <CardDescription>Data on Aniyor platform</CardDescription>
                    </CardHeader>
                    <CardContent className="pt-4 space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground">PAN Number</label>
                        <p className="font-mono font-medium">{gstDetailData.comparison.vendor_profile.pan_number || '-'}</p>
                      </div>
                      <div className={gstDetailData.comparison.gst_api.legal_name !== gstDetailData.comparison.vendor_profile.legal_name ? 'bg-red-50 p-2 rounded border border-red-200' : ''}>
                        <label className="text-xs text-muted-foreground">Business Name</label>
                        <p className="font-medium">{gstDetailData.comparison.vendor_profile.legal_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground">Business Type</label>
                        <p className="font-medium">{gstDetailData.comparison.vendor_profile.business_type || '-'}</p>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> Address
                        </label>
                        <p className="font-medium">
                          {gstDetailData.comparison.vendor_profile.address?.city}, {gstDetailData.comparison.vendor_profile.address?.state}
                          <br />
                          <span className="text-sm text-muted-foreground">
                            Pincode: {gstDetailData.comparison.vendor_profile.address?.pincode}, State: {gstDetailData.comparison.vendor_profile.address?.state_code}
                          </span>
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Match Scores */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Match Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-3 bg-muted rounded">
                        <p className="text-xs text-muted-foreground mb-1">Legal Name</p>
                        <span className={`font-medium ${getMatchScoreColor(gstDetailData.queue_item.match_scores?.legal_name_score || 0)}`}>
                          {Math.round((gstDetailData.queue_item.match_scores?.legal_name_score || 0) * 100)}%
                        </span>
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <p className="text-xs text-muted-foreground mb-1">Trade Name</p>
                        <span className={`font-medium ${getMatchScoreColor(gstDetailData.queue_item.match_scores?.trade_name_score || 0)}`}>
                          {Math.round((gstDetailData.queue_item.match_scores?.trade_name_score || 0) * 100)}%
                        </span>
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <p className="text-xs text-muted-foreground mb-1">Best Match</p>
                        <span className={`font-medium ${getMatchScoreColor(gstDetailData.queue_item.match_scores?.best_name_score || 0)}`}>
                          {Math.round((gstDetailData.queue_item.match_scores?.best_name_score || 0) * 100)}%
                        </span>
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <p className="text-xs text-muted-foreground mb-1">State Match</p>
                        {gstDetailData.queue_item.match_scores?.state_matched ? 
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" /> : 
                          <XCircle className="h-5 w-5 text-red-600 mx-auto" />}
                      </div>
                      <div className="text-center p-3 bg-muted rounded">
                        <p className="text-xs text-muted-foreground mb-1">PAN Match</p>
                        {gstDetailData.queue_item.match_scores?.pan_matched ? 
                          <CheckCircle2 className="h-5 w-5 text-green-600 mx-auto" /> : 
                          <XCircle className="h-5 w-5 text-red-600 mx-auto" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Action Buttons */}
                {gstDetailData.queue_item.status === 'PENDING_MANUAL' && (
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex justify-end gap-4">
                        <Button 
                          variant="outline"
                          onClick={() => setGstActionDialog({ open: true, type: 'reject' })}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject
                        </Button>
                        <Button 
                          onClick={() => setGstActionDialog({ open: true, type: 'approve' })}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckCircle2 className="h-4 w-4 mr-2" />
                          Approve & Update Profile
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <>
                {/* GST Queue Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Pending</p>
                          <p className="text-2xl font-bold text-yellow-600">{gstQueueCounts['PENDING_MANUAL'] || 0}</p>
                        </div>
                        <Clock className="h-8 w-8 text-yellow-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Approved</p>
                          <p className="text-2xl font-bold text-green-600">{gstQueueCounts['APPROVED'] || 0}</p>
                        </div>
                        <CheckCircle2 className="h-8 w-8 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Rejected</p>
                          <p className="text-2xl font-bold text-red-600">{gstQueueCounts['REJECTED'] || 0}</p>
                        </div>
                        <XCircle className="h-8 w-8 text-red-200" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-500">Actions</p>
                          <Button onClick={fetchGstQueue} size="sm" variant="outline" disabled={gstQueueLoading}>
                            <RefreshCw className={`h-4 w-4 mr-1 ${gstQueueLoading ? 'animate-spin' : ''}`} />
                            Refresh
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* GST Queue Filters */}
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex gap-4 flex-wrap">
                      <div className="w-48">
                        <label className="text-xs text-muted-foreground">Status</label>
                        <Select value={gstStatusFilter} onValueChange={setGstStatusFilter}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Statuses" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Statuses</SelectItem>
                            <SelectItem value="PENDING_MANUAL">Pending Review</SelectItem>
                            <SelectItem value="APPROVED">Approved</SelectItem>
                            <SelectItem value="REJECTED">Rejected</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="w-48">
                        <label className="text-xs text-muted-foreground">Priority</label>
                        <Select value={gstPriorityFilter} onValueChange={setGstPriorityFilter}>
                          <SelectTrigger>
                            <SelectValue placeholder="All Priorities" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Priorities</SelectItem>
                            <SelectItem value="HIGH">High Priority</SelectItem>
                            <SelectItem value="LOW">Low Priority</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* GST Queue List */}
                {gstQueueLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : gstQueue.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                      No verification requests found
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {gstQueue.map((item) => (
                      <Card key={item.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <p className="font-medium">{item.vendor_name}</p>
                                {getGstStatusBadge(item.status)}
                                {getGstPriorityBadge(item.priority)}
                              </div>
                              <p className="text-sm font-mono text-muted-foreground">{item.gstin}</p>
                              {item.reasons?.length > 0 && (
                                <p className="text-xs text-red-600 mt-1">{item.reasons[0].split(':')[0]}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                Created: {new Date(item.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchGstDetail(item.id);
                              }}
                              disabled={gstDetailLoading && selectedGstItem === item.id}
                              size="sm"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Review
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* GST Action Dialog */}
      <Dialog open={gstActionDialog.open} onOpenChange={(open) => setGstActionDialog({ ...gstActionDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {gstActionDialog.type === 'approve' ? 'Approve GST Verification' : 'Reject GST Verification'}
            </DialogTitle>
            <DialogDescription>
              {gstActionDialog.type === 'approve' 
                ? 'This will verify the GST details and update the vendor profile.'
                : 'Please provide a reason for rejection.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {gstActionDialog.type === 'approve' && (
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  id="gstUpdateProfile"
                  checked={gstUpdateProfile}
                  onChange={(e) => setGstUpdateProfile(e.target.checked)}
                  className="h-4 w-4"
                />
                <label htmlFor="gstUpdateProfile" className="text-sm">
                  Update vendor profile with GST data
                </label>
              </div>
            )}
            
            {gstActionDialog.type === 'reject' && (
              <div>
                <label className="text-sm font-medium">Rejection Reason *</label>
                <Textarea 
                  value={gstRejectionReason}
                  onChange={(e) => setGstRejectionReason(e.target.value)}
                  placeholder="Explain why this verification is being rejected..."
                  className="mt-1"
                />
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium">Admin Notes (Optional)</label>
              <Textarea 
                value={gstAdminNotes}
                onChange={(e) => setGstAdminNotes(e.target.value)}
                placeholder="Add any internal notes..."
                className="mt-1"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setGstActionDialog({ open: false, type: null })}>
              Cancel
            </Button>
            <Button 
              onClick={handleGstAction}
              disabled={gstActionLoading}
              variant={gstActionDialog.type === 'reject' ? 'destructive' : 'default'}
            >
              {gstActionLoading ? 'Processing...' : (gstActionDialog.type === 'approve' ? 'Approve' : 'Reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Vendor Compliance</DialogTitle>
            <DialogDescription>
              Review and approve or reject vendor compliance upgrade requests
            </DialogDescription>
          </DialogHeader>
          
          {selectedVendor && (
            <div className="space-y-4">
              {/* Vendor ID Badge - NEW */}
              <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded-lg border border-indigo-200">
                <User className="h-4 w-4 text-indigo-600" />
                <span className="text-xs text-indigo-600 font-medium">Vendor ID:</span>
                <code className="text-sm font-mono bg-white px-2 py-0.5 rounded border">
                  {selectedVendor.id}
                </code>
              </div>

              {/* Vendor Header */}
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-semibold text-lg">
                    {selectedVendor.business_name || selectedVendor.name}
                  </h4>
                  <p className="text-sm text-gray-500">{selectedVendor.email}</p>
                  {selectedVendor.mobile && (
                    <p className="text-sm text-gray-500">📞 {selectedVendor.mobile}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  {getTierBadge(selectedVendor.compliance?.current_tier)}
                  {getStatusBadge(selectedVendor.compliance?.status)}
                </div>
              </div>
              
              {/* Upgrade Request Info */}
              {selectedVendor.compliance?.requested_tier && 
               selectedVendor.compliance.requested_tier !== selectedVendor.compliance.current_tier && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h5 className="font-medium text-blue-800 mb-2">Upgrade Request Details</h5>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge className={TIER_INFO[selectedVendor.compliance.current_tier]?.color}>
                      {TIER_INFO[selectedVendor.compliance.current_tier]?.shortName}
                    </Badge>
                    <ArrowUp className="h-5 w-5 text-blue-600" />
                    <Badge className={TIER_INFO[selectedVendor.compliance.requested_tier]?.color}>
                      {TIER_INFO[selectedVendor.compliance.requested_tier]?.shortName}
                    </Badge>
                  </div>
                  <p className="text-sm text-blue-700">
                    {TIER_INFO[selectedVendor.compliance.requested_tier]?.description}
                  </p>
                </div>
              )}
              
              {/* Compliance Information */}
              <div className="border rounded-lg p-4 space-y-3">
                <h5 className="font-medium">Compliance Information</h5>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {selectedVendor.compliance?.pan_number && (
                    <div>
                      <span className="text-gray-500">PAN Number:</span>
                      <p className="font-medium flex items-center gap-1">
                        {selectedVendor.compliance.pan_number}
                        {selectedVendor.compliance.pan_verified && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                      </p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.gst_enrollment_id && (
                    <div>
                      <span className="text-gray-500">Enrollment ID:</span>
                      <p className="font-medium flex items-center gap-1">
                        {selectedVendor.compliance.gst_enrollment_id}
                        {selectedVendor.compliance.enrollment_verified && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                      </p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.gstin && (
                    <div>
                      <span className="text-gray-500">GSTIN:</span>
                      <p className="font-medium flex items-center gap-1">
                        {selectedVendor.compliance.gstin}
                        {selectedVendor.compliance.gst_verified && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                      </p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.gst_business_name && (
                    <div>
                      <span className="text-gray-500">GST Legal Name:</span>
                      <p className="font-medium">{selectedVendor.compliance.gst_business_name}</p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.gst_state && (
                    <div>
                      <span className="text-gray-500">GST State:</span>
                      <p className="font-medium">{selectedVendor.compliance.gst_state}</p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.iec_code && (
                    <div>
                      <span className="text-gray-500">IEC Code:</span>
                      <p className="font-medium flex items-center gap-1">
                        {selectedVendor.compliance.iec_code}
                        {selectedVendor.compliance.iec_verified && (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        )}
                      </p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.iec_holder_name && (
                    <div>
                      <span className="text-gray-500">IEC Holder:</span>
                      <p className="font-medium">{selectedVendor.compliance.iec_holder_name}</p>
                    </div>
                  )}
                  
                  {selectedVendor.compliance?.lut_acknowledged && (
                    <div className="col-span-2">
                      <span className="text-gray-500">LUT Compliance:</span>
                      <p className="font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Vendor has acknowledged LUT requirements
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Business Address Section - NEW */}
              {(selectedVendor.business_address || selectedVendor.pickup_address || selectedVendor.compliance?.gst_address) && (
                <div className="border rounded-lg p-4 bg-gray-50">
                  <h5 className="font-medium mb-3 flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-600" />
                    Business Address
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {selectedVendor.business_address && (
                      <div>
                        <span className="text-gray-500 text-xs uppercase">Registered Address</span>
                        <p className="font-medium">
                          {selectedVendor.business_address.address_line1}
                          {selectedVendor.business_address.address_line2 && `, ${selectedVendor.business_address.address_line2}`}
                        </p>
                        <p className="text-gray-600">
                          {selectedVendor.business_address.city}, {selectedVendor.business_address.state} - {selectedVendor.business_address.pincode}
                        </p>
                      </div>
                    )}
                    {selectedVendor.pickup_address && (
                      <div>
                        <span className="text-gray-500 text-xs uppercase">Pickup Address</span>
                        <p className="font-medium">
                          {selectedVendor.pickup_address.address_line1 || selectedVendor.pickup_address.street}
                          {selectedVendor.pickup_address.address_line2 && `, ${selectedVendor.pickup_address.address_line2}`}
                        </p>
                        <p className="text-gray-600">
                          {selectedVendor.pickup_address.city}, {selectedVendor.pickup_address.state} - {selectedVendor.pickup_address.pincode}
                        </p>
                      </div>
                    )}
                    {selectedVendor.compliance?.gst_address && !selectedVendor.business_address && (
                      <div className="col-span-2">
                        <span className="text-gray-500 text-xs uppercase">GST Registered Address</span>
                        <p className="font-medium">{selectedVendor.compliance.gst_address}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Bank Account Status Section - NEW */}
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <Building className="h-4 w-4 text-gray-600" />
                  Bank Account Status
                </h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Bank Verified:</span>
                    <p className="font-medium flex items-center gap-1">
                      {selectedVendor.bank_verified || selectedVendor.has_bank_account ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-green-600">Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-red-600" />
                          <span className="text-red-600">Not Verified</span>
                        </>
                      )}
                    </p>
                  </div>
                  {selectedVendor.bank_account && (
                    <>
                      <div>
                        <span className="text-gray-500">Bank Name:</span>
                        <p className="font-medium">{selectedVendor.bank_account.bank_name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Account Number:</span>
                        <p className="font-medium font-mono">
                          {selectedVendor.bank_account.account_number 
                            ? `****${selectedVendor.bank_account.account_number.slice(-4)}` 
                            : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">IFSC Code:</span>
                        <p className="font-medium font-mono">{selectedVendor.bank_account.ifsc_code || 'N/A'}</p>
                      </div>
                    </>
                  )}
                  {!selectedVendor.bank_account && !selectedVendor.has_bank_account && (
                    <div className="col-span-2">
                      <p className="text-amber-600 text-sm flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        No bank account details on file
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Contact Information Section - NEW */}
              <div className="border rounded-lg p-4">
                <h5 className="font-medium mb-3 flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-600" />
                  Contact Information
                </h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Contact Person:</span>
                    <p className="font-medium">{selectedVendor.name || selectedVendor.contact_name || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <p className="font-medium">{selectedVendor.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Mobile:</span>
                    <p className="font-medium">{selectedVendor.mobile || selectedVendor.phone || 'N/A'}</p>
                  </div>
                  {selectedVendor.support_email && (
                    <div>
                      <span className="text-gray-500">Support Email:</span>
                      <p className="font-medium">{selectedVendor.support_email}</p>
                    </div>
                  )}
                  {selectedVendor.created_at && (
                    <div>
                      <span className="text-gray-500">Registered On:</span>
                      <p className="font-medium">{new Date(selectedVendor.created_at).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Documents Section */}
              {(selectedVendor.compliance?.gst_doc || selectedVendor.compliance?.iec_doc || 
                selectedVendor.compliance?.documents?.length > 0) && (
                <div className="border rounded-lg p-4">
                  <h5 className="font-medium mb-3">Uploaded Documents</h5>
                  <div className="space-y-2">
                    {selectedVendor.compliance?.gst_doc && (
                      <a 
                        href={selectedVendor.compliance.gst_doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100"
                      >
                        <FileText className="h-5 w-5 text-blue-600" />
                        <span className="flex-1">GST Certificate</span>
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      </a>
                    )}
                    {selectedVendor.compliance?.iec_doc && (
                      <a 
                        href={selectedVendor.compliance.iec_doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded hover:bg-gray-100"
                      >
                        <FileText className="h-5 w-5 text-purple-600" />
                        <span className="flex-1">IEC Certificate</span>
                        <ExternalLink className="h-4 w-4 text-gray-400" />
                      </a>
                    )}
                  </div>
                </div>
              )}
              
              {/* Review Form */}
              {selectedVendor.compliance?.status === 'pending' && (
                <div className="border-t pt-4 space-y-4">
                  <h5 className="font-medium">Review Decision</h5>
                  
                  {/* Decision Buttons */}
                  <div className="flex gap-3">
                    <Button
                      variant={reviewData.approved ? 'default' : 'outline'}
                      className={reviewData.approved ? 'bg-green-600 hover:bg-green-700' : ''}
                      onClick={() => setReviewData(prev => ({ ...prev, approved: true }))}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      variant={!reviewData.approved ? 'destructive' : 'outline'}
                      onClick={() => setReviewData(prev => ({ ...prev, approved: false }))}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                  
                  {/* Admin Notes */}
                  <div>
                    <label className="text-sm font-medium mb-2 block">Admin Notes (optional)</label>
                    <Textarea
                      value={reviewData.admin_notes}
                      onChange={(e) => setReviewData(prev => ({ ...prev, admin_notes: e.target.value }))}
                      placeholder="Add notes about this verification..."
                      rows={2}
                    />
                  </div>
                  
                  {/* Rejection Reason */}
                  {!reviewData.approved && (
                    <div>
                      <label className="text-sm font-medium mb-2 block text-red-700">
                        Rejection Reason *
                      </label>
                      <Textarea
                        value={reviewData.rejection_reason}
                        onChange={(e) => setReviewData(prev => ({ ...prev, rejection_reason: e.target.value }))}
                        placeholder="Explain why this upgrade request is being rejected..."
                        rows={3}
                        className="border-red-200 focus:border-red-400"
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialog(false)}>
              Cancel
            </Button>
            {selectedVendor?.compliance?.status === 'pending' && (
              <Button 
                onClick={handleSubmitReview} 
                disabled={processing}
                className={reviewData.approved ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              >
                {processing ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Processing...</>
                ) : (
                  <>{reviewData.approved ? 'Approve Upgrade' : 'Reject Request'}</>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default AdminVendorCompliance;
